import { request, unwrap } from '../client';
import type {
  ApiResponse,
  CreateProductRequest,
  ProductDto,
  ProductOidcClientDto,
  ProductUpdateResult,
  RoutingDeployOutcome,
  UpdateProductRequest,
  WithRoutingDeploy,
} from '../types';

const BASE = '/api/saas-admin/products';

export const productsApi = {
  async list(): Promise<ProductDto[]> {
    const res = await request<ApiResponse<ProductDto[]>>('GET', BASE);
    return unwrap(res);
  },

  async get(id: number): Promise<ProductDto> {
    const res = await request<ApiResponse<ProductDto>>('GET', `${BASE}/${id}`);
    return unwrap(res);
  },

  // Spec 0059 (saas-auth SASATH-0077) — the distinct group names in use, sorted
  // (initially `["Default"]`). Source for the product Group combobox / list filter.
  async groups(): Promise<string[]> {
    const res = await request<ApiResponse<string[]>>('GET', `${BASE}/groups`);
    return unwrap(res);
  },

  async create(req: CreateProductRequest): Promise<ProductDto> {
    const res = await request<ApiResponse<ProductDto>>('POST', BASE, { json: req });
    return unwrap(res);
  },

  // SASADM-0129 — `dryRun` previews a base-domain rehome without writing. `buildUrl` drops
  // `undefined`, so a normal save sends no query param and behaves exactly as before.
  async update(
    id: number,
    req: UpdateProductRequest,
    opts?: { dryRun?: boolean },
  ): Promise<ProductUpdateResult> {
    const res = await request<ApiResponse<ProductUpdateResult>>('PUT', `${BASE}/${id}`, {
      json: req,
      query: { dryRun: opts?.dryRun ? 'true' : undefined },
    });
    return unwrap(res);
  },

  // SASADM-0176 (SASATH-0392) — the delete re-renders routing and returns the outcome in
  // `data.routingDeploy` (additive): tolerant of an empty body or null data → null.
  async delete(id: number): Promise<RoutingDeployOutcome | null> {
    const res = await request<ApiResponse<WithRoutingDeploy | null> | undefined>(
      'DELETE',
      `${BASE}/${id}`,
    );
    return res?.data?.routingDeploy ?? null;
  },

  // Spec 0062 (saas-auth SASATH-0079); shape corrected in SASADM-0065 — the
  // product → OIDC-clients reverse view: the clients in scope for this product,
  // each tagged with `scope` (PRODUCT/PLATFORM) + `removableFromProduct`. The
  // endpoint returns the client list as a BARE ARRAY in `data`
  // (`ApiResponse<ProductOidcClientDto[]>`), like every other admin list
  // endpoint; `?? []` guards a null/absent `data`. Detach reuses the SASADM-0061
  // `oidcClients.removeAllowedProduct` DELETE (the same allow-list row) — there is
  // no separate remove endpoint on this reverse view.
  async oidcClients(productId: number): Promise<ProductOidcClientDto[]> {
    const res = await request<ApiResponse<ProductOidcClientDto[]>>(
      'GET',
      `${BASE}/${productId}/oidc-clients`,
    );
    return unwrap(res) ?? [];
  },
};
